import React, { useState , useRef,useEffect} from 'react';
import './../../../Styles/Reels/Comment.css';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { toast } from 'react-toastify';
import { useCart } from '../../../CartProvider';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import ProfileAvatar from '../ProfileAvatar/ProfileAvatar';
import CloseIcon from '../../../assets/svg/closeIcon';
import axiosVendorInstance from '../../../axiosVendorInstance';
import { jwtDecode } from 'jwt-decode';

function Comment({
    handleClose,
    id,
    commentsList,
    setCommentsList,
    setCommentsCount
}) {
    const [comment, setComment] = useState('');
    const [editIndex, setEditIndex] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCommentId, setSelectedCommentId] = useState(null);
    const [originalComment, setOriginalComment] = useState('');
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {profileImage, userData} = useCart();
    const token = localStorage.getItem('authToken');
    const decodedToken = token ? jwtDecode(token) : {};

    const isCommentEditable = (createdAt) =>
        dayjs().diff(dayjs(createdAt), 'minute') <= 15;

    const handleProfile = () => navigate('/profile');

    const handleMenuClick = (event, id, createdAt) => {
        if (!isCommentEditable(createdAt)) {
            toast.warn('You can no longer edit or delete this comment.');
            return;
        }
        setAnchorEl(event.currentTarget);
        setSelectedCommentId(id);
    };

    const handleMenuClose = () => setAnchorEl(null);

    const handleEdit = () => {
        const c = commentsList.find((x) => x.ID === selectedCommentId);
        if (c) {
            setComment(c.Comment);
            setOriginalComment(c.Comment);
            setEditIndex(selectedCommentId);
        }
        handleMenuClose();
    };

    const handleDelete = async (commentId) => {
        try {
            await axiosVendorInstance.delete(
                `/comments/user/post/${id}/comment/${commentId}`,
            );
            setCommentsList((prev) => {
                const next = prev.filter((c) => c.ID !== commentId);
                setCommentsCount(next.length);
                return next;
            });
        } catch (err) {
            console.error('Error deleting comment:', err);
        }
    };

    const handleSubmit = async () => {
        if (!comment.trim()) return;

        setLoading(true);
        const currentTime = new Date().toISOString();
        try {
            if (editIndex !== null) {
                const payload = {
                    Comment: comment,
                    Updated_at: currentTime,
                    Updated_by: decodedToken.id,
                };
                await axiosVendorInstance.put(
                    `/comments/user/post/${id}/${editIndex}`,
                    payload,
                );

                setCommentsList((list) =>
                    list.map((c) =>
                        c.ID === editIndex
                            ? { ...c, Comment: comment, Updated_at: currentTime }
                            : c,
                    ),
                );
                setEditIndex(null);
            } else {
                // Add new comment
                const newComment = {
                    Comment: comment,
                    Created_at: currentTime,
                    Created_by: decodedToken.id,
                    Updated_at: currentTime,
                    Updated_by: decodedToken.id,
                    userName: userData?.username,
                    first_name: userData?.first_name,
                    last_name: userData?.last_name,
                    profilePic: userData?.profile_picture,
                };
                const { data } = await axiosVendorInstance.post(
                    `/comments/user/post/${id}`,
                    newComment,
                );
                setCommentsList((prev) => [...prev, data]);
            }

            setComment('');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Save failed');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const commentsEndRef = useRef(null);
    useEffect(() => {
        if (commentsList.length && commentsEndRef.current) {
            commentsEndRef.current.scrollTop = 0;
        }
    }, [commentsList]);

    useEffect(() => {
        const timers = commentsList.map((c) => {
            const diffMin = 15 - dayjs().diff(dayjs(c.Created_at), 'minute');
            if (diffMin <= 0) return null;
            return setTimeout(() => {
                setCommentsList((prev) => [...prev]); // force re‑render
                toast.info('Editing time for a comment has expired.');
            }, diffMin * 60 * 1000);
        });
        return () => timers.forEach((t) => t && clearTimeout(t));
    }, [commentsList, setCommentsList]);

    return (
        <div className="comments-main-container">

            <div className="comment-and-crossIcon-container">
                <Typography id="modal-modal-title" variant="h6" component="h2" className='comment-title'>
                    {t('reels.comments-title')}
                </Typography>

                <CloseIcon onClick={() => handleClose()} className="cross-icon-location" />
            </div>

            {commentsList.length === 0 ? (
                <p className="no-comment">No Comments yet</p>
            ) : (
                <div className="user-comments-list">
                    {commentsList.map((c) => {
                        const updated = c.updated_at || c.Updated_at;
                        const created = c.created_at || c.Created_at;
                        return (
                            <div key={c.ID} className="user-comments-container">
                                {/* user & menu */}
                                <div className="user-profile-and-name">
                                    <ProfileAvatar
                                        firstName={c.first_name}
                                        lastName={c.last_name}
                                        profilePic={c.Profile_Pic}
                                        height={30}
                                        width={30}
                                        fontSize={16}
                                        onClick={handleProfile}
                                    />
                                    <div className="user-name-verified">
                                        <h5>{c.userName}</h5>
                                    </div>

                                    {updated && updated !== created && (
                                        <p className="user-comment-time" style={{color: 'black'}}>Edited</p>
                                    )}
                                    <p className="user-comment-time">
                                        {dayjs(created).format('dddd, h:mm A')}
                                    </p>

                                    {userData?.ID === c.user_id && isCommentEditable(created) && (
                                        <MoreVertIcon
                                            onClick={(e) =>
                                                handleMenuClick(e, c.ID, created)
                                            }
                                            className="three-dot"
                                            fontSize="1.2rem"
                                        />
                                    )}

                                    <Menu
                                        id="basic-menu"
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl) && selectedCommentId === c.ID}
                                        onClose={handleMenuClose}
                                        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                                    >
                                        <MenuItem onClick={handleEdit}>Edit</MenuItem>
                                        <MenuItem onClick={() => handleDelete(selectedCommentId)}>
                      Delete
                                        </MenuItem>
                                    </Menu>
                                </div>

                                <div className="user-comment">
                                    <p className="user-comment-text">{c.Comment}</p>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={commentsEndRef} />
                </div>
            )}

            <div className="comment-input-container">
                <ProfileAvatar
                    firstName={userData?.first_name}
                    lastName={userData?.last_name}
                    profilePic={profileImage}
                    height={30}
                    width={40}
                    fontSize={16}
                    onClick={handleProfile}
                />

                <input
                    className="comment-input"
                    type="text"
                    placeholder="Add a Comment..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading && comment.trim()) {
                            handleSubmit();
                        }
                    }}
                />
                <Button
                    variant="contained"
                    className="comment-post-button"
                    onClick={handleSubmit}
                    disabled={
                        loading ||
            !comment.trim() ||
            (editIndex !== null && comment.trim() === originalComment.trim())
                    }
                    style={{
                        cursor:
              loading ||
              !comment.trim() ||
              (editIndex !== null && comment.trim() === originalComment.trim())
                  ? 'not-allowed'
                  : 'pointer',
                        opacity:
              loading ||
              !comment.trim() ||
              (editIndex !== null && comment.trim() === originalComment.trim())
                  ? 0.7
                  : 1,
                        backgroundColor: '#1976d2',
                        color: '#fff',
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : editIndex !== null ? (
                        'Update'
                    ) : (
                        'Post'
                    )}
                </Button>
            </div>
        </div>
    );
}

export default Comment;
